<template>
  <div v-show="playlist.length !== 0">
    <div
      v-show="showMiniPlayer"
      class="pointer-events-auto float-right m-4 flex h-14 w-14 cursor-pointer items-center justify-center bg-primary-400 transition-colors duration-200 ease-in-out hover:bg-primary-100"
      @click="showMiniPlayer = false"
    >
      <IconMedia class="h-8 w-8 text-shade-50" />
    </div>
    <div v-show="!showMiniPlayer">
      <div
        ref="waveformContainer"
        class="pointer-events-none relative flex h-16 w-full justify-between gap-1.5 motion-reduce:hidden"
      >
        <ClientOnly> </ClientOnly>
      </div>
      <div
        class="pointer-events-auto w-full bg-primary-400 p-3 text-shade-50 lg:p-4"
      >
        <audio
          id="audioPlayer"
          preload="metadata"
          ref="audioPlayer"
          controls
          class="hidden"
          @loadedmetadata="handleLoadedMetadata"
          @timeupdate="onTimeUpdate"
        >
          <source :src="currentSong" />
        </audio>

        <div class="flex flex-col md:flex-row md:items-center md:gap-4">
          <div class="flex-auto md:order-10">
            <div class="flex items-center gap-2">
              <IconHeadset class="h-6 w-6" />
              <h4 class="flex-1 text-mobile-lg font-semibold md:text-lg">
                {{ currentTitle }}
              </h4>
              <p class="text-mobile-lg md:text-lg">
                {{ 'Artiste' }}
              </p>
            </div>
            <div class="mt-2 flex items-center gap-2">
              <p class="select-none text-mobile-lg md:text-lg">
                {{ fmtMSSeconds(currentTime) }}
              </p>
              <input
                type="range"
                v-model="currentTime"
                :max="duration"
                class="h-2 flex-1 appearance-none bg-shade-alpha-50 p-0 outline-none webkit-slider-thumb:h-6 webkit-slider-thumb:w-0.5 webkit-slider-thumb:appearance-none webkit-slider-thumb:border-none webkit-slider-thumb:bg-shade-50 webkit-slider-runnable-track:cursor-pointer webkit-slider-runnable-track:bg-transparent moz-range-track:bg-transparent moz-range-thumb:h-6 moz-range-thumb:w-0.5 moz-range-thumb:rounded-none moz-range-thumb:border-none moz-range-thumb:bg-shade-50 moz-range-progress:bg-transparent ms-track:bg-transparent ms-thumb:h-6 ms-thumb:w-0.5 ms-thumb:bg-shade-50 ms-tooltip:hidden ms-fill-lower:bg-transparent ms-fill-upper:bg-transparent"
              />
              <p class="select-none text-mobile-lg md:text-lg">
                {{ fmtMSSeconds(duration) }}
              </p>
            </div>
          </div>
          <div class="mt-3 flex items-center justify-between md:mt-0">
            <button
              class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="index === 0 || playlist.length === 0"
              @click="previousSong"
            >
              <IconBack class="z-10 h-8 w-8" />
              <div
                class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
              ></div>
            </button>
            <button
              class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
              @click="toggleState"
            >
              <IconPlay v-if="isPaused" class="z-10 h-8 w-8 text-primary-400" />
              <IconPause v-else class="z-10 h-8 w-8 text-primary-400" />

              <div
                class="absolute h-full w-full scale-75 bg-shade-50 transition-all duration-200 ease-in-out group-hover:scale-100"
              ></div>
            </button>
            <button
              class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="index === playlist.length - 1 || playlist.length === 0"
              @click="nextSong"
            >
              <IconForward class="z-10 h-8 w-8" />
              <div
                class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
              ></div>
            </button>
            <FeedbackPopover position="top-center">
              <template #button>
                <button
                  class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <IconVolume class="z-10 h-8 w-8" />
                  <div
                    class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
                  ></div>
                </button>
              </template>
              <div class="inline-block h-48 w-2 p-0 leading-none">
                <div class="w-6">
                  <BasicSlider
                    v-model="currentVolume"
                    min="0"
                    max="1"
                    step="0.01"
                    class="align-text-top"
                  />
                </div>
              </div>
            </FeedbackPopover>
            <button
              class="group relative hidden h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 lg:flex"
            >
              <IconLoopPlaylist class="z-10 h-8 w-8" />
              <div
                class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
              ></div>
            </button>
            <FeedbackMenu
              position="top-center"
              :items="playlist"
              theme="player"
            >
              <template #button>
                <button
                  class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 md:hidden"
                >
                  <IconMenu class="z-10 h-8 w-8" />
                  <div
                    class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
                  ></div>
                </button>
              </template>
            </FeedbackMenu>
            <button
              class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50 md:hidden"
              @click="showMiniPlayer = true"
            >
              <IconChevronDown class="z-10 h-8 w-8" />
              <div
                class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
              ></div>
            </button>
          </div>
          <!-- Desktop menu after the player, hidden on mobile -->
          <div
            class="hidden items-center justify-between md:order-last md:flex"
          >
            <FeedbackMenu
              position="top-center"
              :items="playlist"
              theme="player"
            >
              <template #button>
                <button
                  class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <IconMenu class="z-10 h-8 w-8" />
                  <div
                    class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
                  ></div>
                </button>
              </template>
            </FeedbackMenu>
            <button
              class="group relative flex h-14 w-14 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
              @click="showMiniPlayer = true"
            >
              <IconChevronDown class="z-10 h-8 w-8" />
              <div
                class="absolute h-full w-full scale-75 bg-shade-alpha-50 opacity-0 transition-all duration-200 ease-in-out group-hover:scale-100 group-hover:opacity-100"
              ></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import IconMedia from '~/assets/icon/media/Media.svg'
import IconHeadset from '~/assets/icon/media/Headset.svg'
import IconBack from '~/assets/icon/media/Back.svg'
import IconPlay from '~/assets/icon/media/Play-Alt.svg'
import IconPause from '~/assets/icon/media/Pause.svg'
import IconForward from '~/assets/icon/media/Forward.svg'
import IconLoopPlaylist from '~/assets/icon/media/Loop-Playlist.svg'
import IconMenu from '~/assets/icon/general/Menu.svg'
import IconChevronDown from '~/assets/icon/navigation/Chevron/Down.svg'
import IconVolume from '~/assets/icon/media/Volume.svg'
import { fmtMSSeconds } from '~/const/conversion'

// State
let defaultAudio = useState('audio')
const currentPlayingMediaId = useState('currentPlayingMediaId')

// Template ref
const audioPlayer = ref(null)

// Reactive values
const currentVolume = ref(1)
const currentTime = ref(0)
const duration = ref(0)
const index = ref(0)
const isPaused = ref(true)
const playlist = ref<Media[]>([
  ...(defaultAudio.value?.files?.[0] ? [defaultAudio.value] : []),
])
const showMiniPlayer = ref(true)

const { locale } = useI18n()
const currentSong = computed({
  set(newValue) {
    return newValue
  },
  get() {
    return playlist.value[index.value]?.files?.[0]?.getUrl
  },
})
const currentTitle = computed(() => {
  const media = playlist.value[index.value]
  if (locale.value === 'fr' && media?.title) {
    return media?.title
  } else if (locale.value === 'en' && media?.titleEn) {
    return media?.titleEn
  } else {
    return media?.files?.[0]?.originalFileName
  }
})

// Event Listener
const { $event } = useNuxtApp()

watch(currentPlayingMediaId, (newId) => {
  if (newId !== playlist.value[index.value]?.id) {
    audioPlayer.value.pause()
    isPaused.value = true
  }
})
const { $listen } = useNuxtApp()
$listen('audio', (data) => {
  switch (data.type) {
    case 'play':
      if (currentSong.value === data.media.getUrl) {
        if (!audioPlayer.value?.src) {
          audioPlayer.value.src = currentSong.value
        }
        currentPlayingMediaId.value = data.id
        audioPlayer.value.play()
      } else {
        playlist.value = [
          ...playlist.value.slice(0, index.value + 1),
          { ...data.media, files: [data.media] },
          ...playlist.value.slice(index.value + 1),
        ]
        currentSong.value = playlist.value[index.value]?.files?.[0]?.getUrl
        if (playlist.value.length === 1) {
          index.value = 0
        } else {
          index.value += 1
        }
        currentPlayingMediaId.value = playlist.value[index.value]?.id
        audioPlayer.value.load()
        audioPlayer.value.play()
        isPaused.value = false
        $event('play', { value: currentSong.value })
      }
      showMiniPlayer.value = false
      isPaused.value = false
      break
    case 'add_to_playlist':
      playlist.value.push(data.media)
      break
    case 'pause':
      if (currentPlayingMediaId.value === playlist.value[index.value]?.id) {
        currentPlayingMediaId.value = null
      }
      toggleState()
      break
    default:
      break
  }
})

const toggleState = () => {
  if (isPaused.value) {
    audioPlayer.value.play()
    isPaused.value = false
    $event('play', { value: currentSong.value })
  } else {
    if (currentPlayingMediaId.value === currentSong.value?.id) {
      currentPlayingMediaId.value = null
    }
    audioPlayer.value.pause()
    isPaused.value = true
    $event('pause')
  }
}
// Player Functions
const previousSong = () => {
  index.value -= 1
  audioPlayer.value.pause()
  audioPlayer.value.load()
  audioPlayer.value.play()
}
const nextSong = () => {
  index.value += 1
  audioPlayer.value.pause()
  audioPlayer.value.load()
  audioPlayer.value.play()
}
// Volume Management
watch(currentVolume, (newVolume) => {
  audioPlayer.value.volume = newVolume
  if (newVolume <= 0) {
    audioPlayer.value.muted = true
  } else {
    audioPlayer.value.muted = false
  }
})
// Time Management
const handleLoadedMetadata = () => {
  duration.value = Math.round(audioPlayer.value.duration)
}
const onTimeUpdate = () => {
  currentTime.value = Math.round(audioPlayer.value.currentTime)
  $event('duration', {
    type: 'bind',
    duration: audioPlayer.value.duration,
    currentTime: audioPlayer.value.currentTime,
  })
}

$listen('durationSecondPlayer', (newTime) => {
  currentTime.value = newTime
})
watch(currentTime, (newValue) => {
  const diff = Math.abs(newValue - audioPlayer.value.currentTime)
  if (diff > 2) {
    audioPlayer.value.currentTime = newValue
  }
})
</script>
